import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Alert, AlertDescription, AlertTitle,
	Button,
	Dialog, DialogContent, DialogHeader,
	Input,
} from '@rentflowdev/ui'
import { AlertCircleIcon } from 'lucide-react'
import { useConnectTelegramBotMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'

interface TelegramConnectDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** @username of the already connected bot, when reconfiguring. */
	connectedBotName?: string
}

/**
 * BYOB flow: the account owner creates their own bot via @BotFather and
 * pastes the token here. Clients then write to that bot, pick a property and
 * the conversation lands in the CRM as a lead — replies go out from the deal
 * chat through the same bot.
 */
export function TelegramConnectDialog({ open, onOpenChange, connectedBotName }: TelegramConnectDialogProps) {
	const { t } = useTranslation()
	const [token, setToken] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [connectBot, { isLoading }] = useConnectTelegramBotMutation()

	async function handleConnect() {
		setError(null)
		try {
			await connectBot({ botToken: token.trim() }).unwrap()
			setToken('')
			onOpenChange(false)
		} catch (err) {
			setError(extractErrorMessage(err) ?? t('settings.integrations.telegramDialog.errors.generic'))
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent showCloseButton={false} className="sm:max-w-md">
				<DialogHeader title={t('settings.integrations.telegramDialog.title')} />

				<div className="flex flex-col gap-4 px-6 pb-6">
					{connectedBotName && (
						<p className="text-sm text-(--color-muted-foreground)">
							{t('settings.integrations.telegramDialog.connectedAs', { bot: connectedBotName })}
						</p>
					)}

					<ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-(--color-foreground)">
						<li>{t('settings.integrations.telegramDialog.steps.createBot')}</li>
						<li>{t('settings.integrations.telegramDialog.steps.copyToken')}</li>
						<li>{t('settings.integrations.telegramDialog.steps.pasteToken')}</li>
						<li>{t('settings.integrations.telegramDialog.steps.share')}</li>
					</ol>

					{error && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertTitle>{t('settings.integrations.telegramDialog.errors.title')}</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}

					<Input
						value={token}
						onChange={(e) => setToken(e.target.value)}
						placeholder={t('settings.integrations.telegramDialog.tokenPlaceholder')}
						autoFocus
					/>

					<div className="flex justify-end gap-2">
						<Button variant="secondary" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button
							disabled={!token.trim() || isLoading}
							loading={isLoading}
							onClick={() => void handleConnect()}
						>
							{t('settings.integrations.telegramDialog.connect')}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
